import React from 'react';
import {
  Button,
  Card,
  Col,
  Form,
  Input,
  Row,
  Space,
  Table,
  Tag,
  Typography,
  Popconfirm,
} from 'antd';
import { RollbackOutlined, SearchOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { PendingItem, r5d6Api } from '../../services/r5d6Api';
import { useR5D6Reversal } from './hooks/useR5D6Reversal';
import {
  BatchAcceptanceBridge,
  pwr5d6f1AcceptanceManifest,
} from '../../features/acceptance';
import type { AcceptanceManifest } from '../../features/acceptance';

const { Title, Text } = Typography;
const acceptanceActionIds = {
  list: 'PWD5M6F1-ACT-QUERY',
  writeoff: 'PWD5M6F1-ACT-REVERSE',
};
void acceptanceActionIds;

// ============================================================
// Helpers
// ============================================================

function renderStatus(status: string): React.ReactNode {
  if (!status || status === '') return <Tag color="default">未送審</Tag>;
  if (status === 'P') return <Tag color="blue">已核准</Tag>;
  if (status === 'N') return <Tag color="red">已沖銷</Tag>;
  return <Tag color="orange">{status}</Tag>;
}

// ============================================================
// Main Component
// ============================================================

interface R5D6ReversalProps {
  api?: typeof r5d6Api;
  manifest?: AcceptanceManifest;
}

const R5D6Reversal: React.FC<R5D6ReversalProps> = ({
  api = r5d6Api,
  manifest = pwr5d6f1AcceptanceManifest,
}) => {
  const activeManifest = manifest;
  const {
    loading,
    reversing,
    pendingItems,
    total,
    selectedKeys,
    filter,
    setFilter,
    setSelectedKeys,
    fetchPending,
    batchReverse,
  } = useR5D6Reversal(api);

  // ============================================================
  // Table columns
  // ============================================================
  const columns: ColumnsType<PendingItem> = [
    {
      title: <span data-contract-field="fd160_01">退出單號</span>,
      dataIndex: 'doc_no',
      key: 'doc_no',
      width: 110,
      render: (text: string) => <Text code>{text}</Text>,
    },
    {
      title: <span data-contract-field="f104_12">分包商名稱</span>,
      dataIndex: 'vendor_name',
      key: 'vendor_name',
      ellipsis: true,
    },
    {
      title: <span data-contract-field="fd160_11">繳庫單號</span>,
      dataIndex: 'receiving_doc_no',
      key: 'receiving_doc_no',
      width: 110,
    },
    {
      title: <span data-contract-field="fd160_12">退回日期</span>,
      dataIndex: 'return_date',
      key: 'return_date',
      width: 120,
      render: (date: string | null) =>
        date ? new Date(date).toLocaleDateString('zh-TW') : '—',
    },
    {
      title: <span data-contract-field="fd160_80">狀態</span>,
      dataIndex: 'status',
      key: 'status',
      width: 90,
      align: 'center',
      render: renderStatus,
    },
  ];

  // ============================================================
  // Row selection
  // ============================================================
  const rowSelection = {
    selectedRowKeys: selectedKeys,
    onChange: (keys: React.Key[]) => setSelectedKeys(keys as string[]),
  };

  // ============================================================
  // Filter form handlers
  // ============================================================
  const handleSearch = () => { fetchPending(); };

  const handleFilterChange = (field: 'docStart' | 'docEnd', value: string) => {
    setFilter({ ...filter, [field]: value });
  };

  // ============================================================
  // Render
  // ============================================================
  return (
    <div style={{ padding: 16 }}>
      <Title level={4} style={{ marginBottom: 16 }}>繳庫退出單沖銷 (pwr5d6f1)</Title>

      {/* Filter Bar */}
      <Card size="small" style={{ marginBottom: 12 }}>
        <Form layout="inline" size="small">
          <Form.Item label={<span data-contract-field="query_doc_start">單號起始</span>}>
            <Input
              value={filter.docStart}
              onChange={(e) => handleFilterChange('docStart', e.target.value)}
              placeholder="例：D24001A"
              style={{ width: 130 }}
              onPressEnter={handleSearch}
            />
          </Form.Item>
          <Form.Item label={<span data-contract-field="query_doc_end">單號結束</span>}>
            <Input
              value={filter.docEnd}
              onChange={(e) => handleFilterChange('docEnd', e.target.value)}
              placeholder="例：D24099Z"
              style={{ width: 130 }}
              onPressEnter={handleSearch}
            />
          </Form.Item>
          <Form.Item>
            <span data-acceptance-action="PWR5D6-ACT-QUERY">
            <Button
              type="primary"
              icon={<SearchOutlined />}
              data-acceptance-action="PWD5M6F1-ACT-QUERY"
              onClick={handleSearch}
              loading={loading}
            >
              查詢
            </Button>
            </span>
          </Form.Item>
        </Form>
      </Card>

      {/* Summary & Actions */}
      <Card size="small" style={{ marginBottom: 12 }}>
        <Row justify="space-between" align="middle">
          <Col>
            <Space>
              <Text type="secondary">
                共 <strong>{total}</strong> 筆待沖銷
              </Text>
              {selectedKeys.length > 0 && (
                <Text type="secondary">
                  已勾選 <strong>{selectedKeys.length}</strong> 筆
                </Text>
              )}
            </Space>
          </Col>
          <Col>
            <Popconfirm
              title={`確認沖銷 ${selectedKeys.length} 筆退出單？沖銷後狀態將設為 N，無法還原。`}
              onConfirm={batchReverse}
              okText="確認沖銷"
              cancelText="取消"
              disabled={selectedKeys.length === 0}
              okButtonProps={{ danger: true }}
            >
              <span data-acceptance-action="PWR5D6-ACT-REVERSE">
              <Button
                danger
                data-acceptance-action="PWD5M6F1-ACT-REVERSE"
                icon={<RollbackOutlined />}
                loading={reversing}
                disabled={selectedKeys.length === 0}
              >
                批量沖銷
              </Button>
              </span>
            </Popconfirm>
          </Col>
        </Row>
      </Card>

      {/* Data Table */}
      <Table<PendingItem>
        rowKey="doc_no"
        columns={columns}
        dataSource={pendingItems}
        loading={loading}
        rowSelection={rowSelection}
        size="small"
        pagination={{
          pageSize: 50,
          showSizeChanger: false,
          showTotal: (t) => `共 ${t} 筆`,
        }}
        scroll={{ x: 700 }}
        locale={{ emptyText: '無待沖銷資料' }}
      />
      <BatchAcceptanceBridge
        manifest={activeManifest}
        records={pendingItems}
        selectedKeys={selectedKeys}
        keyOf={(item) => item.doc_no}
        queryExact={async (key) => (await api.getPending({ docStart: key, docEnd: key })).items?.[0] || null}
        lastApiResponse={pendingItems}
        loading={loading || reversing}
        onAutoFill={() => pendingItems[0] && setSelectedKeys([pendingItems[0].doc_no])}
        autoFillDisabled={pendingItems.length === 0}
        autoFillReason="只選取正式 API 回傳的第一筆繳庫退出沖銷候選。"
        screenDraft={{ filter, selected_items: selectedKeys }}
      />
    </div>
  );
};

export default R5D6Reversal;
